import { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { Button } from './ui/button';
import {
  LayoutDashboard, Package, FolderTree, Users, ClipboardList,
  Warehouse, Percent, ChevronLeft, ChevronRight, LogOut, ArrowLeft, Menu, X, FileText, RotateCcw, AlertTriangle, CalendarClock, BarChart3, BarChart2
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ADMIN_NAV = [
  { href: '/admin', label: 'Dashboard', icon: LayoutDashboard },
  { href: '/admin/productos', label: 'Productos', icon: Package },
  { href: '/admin/categorias', label: 'Categorías', icon: FolderTree },
  { href: '/admin/ordenes', label: 'Órdenes', icon: ClipboardList },
  { href: '/admin/ndr', label: 'NDR', icon: AlertTriangle, ndr: true },
  { href: '/admin/devoluciones', label: 'Devoluciones', icon: RotateCcw },
  { href: '/admin/usuarios', label: 'Usuarios', icon: Users },
  { href: '/admin/inventario', label: 'Inventario', icon: Warehouse },
  { href: '/admin/analitica-envios', label: 'Analítica de Envíos', icon: BarChart3 },
  { href: '/admin/analitica-web', label: 'Analítica Web', icon: BarChart2 },
  { href: '/admin/recolecciones', label: 'Recolecciones', icon: CalendarClock },
  { href: '/admin/descuentos', label: 'Descuentos', icon: Percent },
  { href: '/admin/facturacion', label: 'Facturación', icon: FileText },
];

export function AdminShell({ children }) {
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [ndrPending, setNdrPending] = useState(0);
  const { user, logout, token } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  // Poll NDR count every 60s so the red badge is fresh
  useEffect(() => {
    if (!token) return;
    let timer;
    const load = () => {
      axios.get(`${API}/envia/admin/ndr`, { headers: { Authorization: `Bearer ${token}` } })
        .then(r => {
          const list = Array.isArray(r.data) ? r.data : [];
          setNdrPending(list.filter(c => !c.ndr_action).length);
        })
        .catch(() => {});
    };
    load();
    timer = setInterval(load, 60000);
    return () => clearInterval(timer);
  }, [token, location.pathname]);

  const isActive = (href) => {
    if (href === '/admin') return location.pathname === '/admin';
    return location.pathname.startsWith(href);
  };

  const SidebarContent = ({ mobile = false }) => (
    <div className="flex flex-col h-full">
      <div className="p-4 border-b border-white/10 flex items-center justify-between">
        {!collapsed && <img src="/logo-kind.png" alt="KIND" className="h-7 w-auto" />}
        {mobile && <Button variant="ghost" size="sm" onClick={() => setMobileOpen(false)} className="text-white hover:bg-white/10"><X className="h-4 w-4" /></Button>}
      </div>
      <nav className="flex-1 p-2 space-y-0.5 overflow-y-auto">
        {ADMIN_NAV.map(item => {
          const Icon = item.icon;
          return (
            <Link
              key={item.href}
              to={item.href}
              onClick={() => mobile && setMobileOpen(false)}
              data-testid={`admin-nav-${item.label.toLowerCase()}`}
              className={`relative flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium transition-colors duration-150 ${
                isActive(item.href)
                  ? 'bg-white/10 text-white'
                  : 'text-gray-400 hover:bg-white/5 hover:text-white'
              }`}
            >
              <Icon className="h-4 w-4 flex-shrink-0" />
              {(!collapsed || mobile) && <span className="flex-1">{item.label}</span>}
              {item.ndr && ndrPending > 0 && (!collapsed || mobile) && (
                <span className="inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-red-500 text-white text-[10px] font-semibold tabular-nums" data-testid="admin-nav-ndr-badge">
                  {ndrPending}
                </span>
              )}
              {item.ndr && ndrPending > 0 && collapsed && !mobile && (
                <span className="absolute top-1 right-1 h-2 w-2 rounded-full bg-red-500" data-testid="admin-nav-ndr-dot" />
              )}
            </Link>
          );
        })}
      </nav>
      <div className="p-2 border-t border-white/10 space-y-1">
        <Link to="/" onClick={() => mobile && setMobileOpen(false)}>
          <Button variant="ghost" size="sm" className={`w-full text-gray-400 hover:bg-white/5 hover:text-white ${collapsed && !mobile ? 'px-2' : 'justify-start gap-2'}`}>
            <ArrowLeft className="h-4 w-4" /> {(!collapsed || mobile) && 'Volver al Sitio'}
          </Button>
        </Link>
        <Button variant="ghost" size="sm" onClick={() => { logout(); navigate('/'); }} className={`w-full text-gray-400 hover:bg-white/5 hover:text-white ${collapsed && !mobile ? 'px-2' : 'justify-start gap-2'}`}>
          <LogOut className="h-4 w-4" /> {(!collapsed || mobile) && 'Cerrar Sesión'}
        </Button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-secondary/30 flex">
      {/* Desktop Sidebar */}
      <aside className={`hidden lg:flex flex-col bg-[#010101] border-r border-white/10 transition-all duration-200 ${collapsed ? 'w-16' : 'w-56'} flex-shrink-0`}>
        <SidebarContent />
        <button
          onClick={() => setCollapsed(!collapsed)}
          className="p-2 border-t border-white/10 flex items-center justify-center text-gray-400 hover:text-white transition-colors duration-150"
        >
          {collapsed ? <ChevronRight className="h-4 w-4" /> : <ChevronLeft className="h-4 w-4" />}
        </button>
      </aside>

      {/* Mobile Sidebar Overlay */}
      {mobileOpen && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div className="fixed inset-0 bg-black/60" onClick={() => setMobileOpen(false)} />
          <div className="fixed left-0 top-0 h-full w-64 bg-[#010101] shadow-xl z-50 border-r border-white/10">
            <SidebarContent mobile />
          </div>
        </div>
      )}

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-w-0">
        <div className="sticky top-0 z-40 bg-background/95 backdrop-blur-sm border-b">
          <div className="flex h-14 items-center gap-4 px-4 lg:px-6">
            <Button variant="ghost" size="sm" className="lg:hidden" onClick={() => setMobileOpen(true)}>
              <Menu className="h-5 w-5" />
            </Button>
            <div className="flex-1" />
            <span className="text-sm text-muted-foreground">{user?.name}</span>
          </div>
        </div>
        <main className="flex-1 p-4 lg:p-6 max-w-[1400px] w-full">
          {children}
        </main>
      </div>
    </div>
  );
}
